import React, { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Trash2, ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import { useSiteSettings } from "../lib/site-settings";
import BactWaterUpsell from "../components/BactWaterUpsell";
import { BACT_SKUS } from "../lib/bactPrompt";
import RecommendedProducts from "../components/RecommendedProducts";
import GiftProgressBadge from "../components/GiftProgressBadge";
import PerksLadder from "../components/PerksLadder";
import { retaPromo, B3G1_LABEL, B3G1_SKUS, useB3G1Strengths, joinStrengths } from "../lib/promo";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, addItem } = useCart();
  const { free_shipping_threshold } = useSiteSettings();
  const b3g1Strengths = useB3G1Strengths();
  const b3g1List = joinStrengths(b3g1Strengths);
  const [params, setParams] = useSearchParams();
  const restoreId = params.get("restore");
  const restoredRef = useRef(false);

  // Abandonment emails link to /cart?restore=<cart_id> so the basket rebuilds on any device.
  useEffect(() => {
    if (!restoreId || restoredRef.current) return;
    restoredRef.current = true;
    (async () => {
      try {
        if (items.length === 0) {
          const { data } = await api.get(`/cart/restore/${restoreId}`);
          for (const line of data.items || []) addItem(line.product, line.quantity);
          if ((data.items || []).length) toast.success("Your basket has been restored");
        }
      } catch {
        toast.error("We couldn't restore that basket — it may have expired");
      } finally {
        params.delete("restore");
        setParams(params, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreId]);

  const hasPeptide = items.some((i) => !BACT_SKUS.includes(i.sku));
  const promo = retaPromo(items);
  const netSubtotal = subtotal - promo.discount;
  const shipFree = netSubtotal >= Number(free_shipping_threshold || 50);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <div className="pg-micro mb-3">// Basket</div>
        <h1 className="text-[#F7FAFC] text-4xl pg-display mb-3" style={{ fontFamily: "'Exo 2'" }}>Your basket is empty</h1>
        <p className="text-[#9CA8B8] mb-8">Browse the research catalogue to add items.</p>
        <Link to="/products" className="pg-btn-primary">Shop Catalogue <ArrowRight size={16} /></Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="cart-page">
      <div className="pg-micro mb-3">// Basket</div>
      <h1 className="text-[#F7FAFC] text-4xl pg-display mb-10" style={{ fontFamily: "'Exo 2'" }}>Your Basket</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-3">
          {items.map((item) => (
            <div key={item.sku} className="pg-card p-5 flex flex-wrap items-center gap-4" data-testid={`cart-item-${item.sku}`}>
              <div className="w-16 h-16 shrink-0 border border-[#00C2D6]/30 rounded-sm flex items-center justify-center pg-mono text-[10px] text-[#00C2D6] tracking-[0.18em]">
                {item.sku.split("-")[1]}
              </div>
              <div className="flex-1 min-w-[140px]">
                <div className="text-[#F7FAFC] pg-heading text-lg" style={{ fontFamily: "'Rajdhani'" }}>
                  {item.name} {item.strength}
                </div>
                <div className="pg-mono text-[11px] text-[#9CA8B8] uppercase tracking-[0.18em] break-words">{item.sku} · Research use only</div>
              </div>
              <div className="flex items-center gap-3 ml-auto">
              <div className="flex items-center gap-2">
                <button onClick={() => updateQty(item.sku, item.quantity - 1)} className="w-8 h-8 border border-[#00C2D6]/30 text-[#F7FAFC] hover:border-[#00C2D6]" data-testid={`cart-dec-${item.sku}`}>−</button>
                <span className="pg-mono text-[#F7FAFC] w-8 text-center" data-testid={`cart-qty-${item.sku}`}>{item.quantity}</span>
                <button onClick={() => updateQty(item.sku, item.quantity + 1)} className="w-8 h-8 border border-[#00C2D6]/30 text-[#F7FAFC] hover:border-[#00C2D6]" data-testid={`cart-inc-${item.sku}`}>+</button>
              </div>
              <div className="text-right min-w-[80px]">
                <div className="text-[#F7FAFC] font-bold">£{(item.price * item.quantity).toFixed(2)}</div>
                <div className="text-[#9CA8B8] text-xs pg-mono">£{item.price.toFixed(2)} ea</div>
                {item.base_price && item.base_price > item.price && (
                  <div className="text-[#A6FF00] text-[10px] pg-mono uppercase tracking-[0.18em] mt-0.5">
                    Save £{((item.base_price - item.price) * item.quantity).toFixed(2)}
                  </div>
                )}
              </div>
              <button onClick={() => removeItem(item.sku)} className="text-[#9CA8B8] hover:text-red-400 transition-colors" aria-label="Remove" data-testid={`cart-remove-${item.sku}`}>
                <Trash2 size={16} />
              </button>
              </div>
            </div>
          ))}

          {promo.units > 0 && promo.remainder > 0 && (
            <div className="mt-4 p-4 rounded-sm border border-[#A6FF00]/40 bg-[#A6FF00]/[0.07]" data-testid="reta-promo-nudge">
              <div className="text-[#A6FF00] pg-mono text-[11px] uppercase tracking-[0.18em] font-bold mb-1">
                Buy 3 Get 1 Free — Retatrutide{b3g1List ? ` ${b3g1List}` : ""}
              </div>
              {promo.remainder === 3 ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="text-[#D5DCE8] text-sm flex-1">
                    You've bought 3 — your <strong className="text-[#A6FF00]">4th vial is FREE</strong>. Add it now, or we'll add it for you when you order.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const cheapest = items.filter((i) => B3G1_SKUS.includes(i.sku)).sort((a, b) => a.price - b.price)[0];
                      if (cheapest) updateQty(cheapest.sku, cheapest.quantity + 1);
                    }}
                    className="pg-btn-primary text-xs px-4 py-2 whitespace-nowrap"
                    data-testid="reta-promo-add-free"
                  >
                    Add my free vial
                  </button>
                </div>
              ) : (
                <div className="text-[#D5DCE8] text-sm">
                  Add {4 - promo.remainder} more Retatrutide vial{4 - promo.remainder > 1 ? "s" : ""}{b3g1List ? ` (${joinStrengths(b3g1Strengths, ", ", " or ")}, mix & match)` : ""} and the cheapest one is <strong className="text-[#A6FF00]">FREE</strong>.
                </div>
              )}
            </div>
          )}
          {promo.freeCount > 0 && (
            <div className="mt-4 p-4 rounded-sm border border-[#A6FF00]/40 bg-[#A6FF00]/[0.07]" data-testid="reta-promo-applied-banner">
              <div className="text-[#A6FF00] pg-mono text-[11px] uppercase tracking-[0.18em] font-bold">
                &#10003; Buy 3 Get 1 Free applied — {promo.freeCount} vial{promo.freeCount > 1 ? "s" : ""} free (−£{promo.discount.toFixed(2)})
              </div>
            </div>
          )}

          {hasPeptide && !items.some((i) => BACT_SKUS.includes(i.sku)) && (
            <div className="mt-4">
              <div className="pg-micro mb-2">// Frequently bought together</div>
              <BactWaterUpsell items={items} addItem={addItem} hasPeptide={true} />
            </div>
          )}
        </div>

        <div className="lg:col-span-4">
          <div className="pg-card p-6 sticky top-24">
            <div className="pg-micro mb-4">// Order Summary</div>
            <PerksLadder subtotal={netSubtotal} />
            <div className="flex justify-between text-[#9CA8B8] text-sm mb-2">
              <span>Subtotal</span>
              <span className="text-[#F7FAFC]" data-testid="cart-subtotal">£{subtotal.toFixed(2)}</span>
            </div>
            {promo.discount > 0 && (
              <div className="flex justify-between text-sm mb-2" data-testid="cart-promo-row">
                <span className="text-[#A6FF00]">{B3G1_LABEL}</span>
                <span className="text-[#A6FF00]">−£{promo.discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-[#9CA8B8] text-sm mb-4">
              <span>Shipping</span>
              <span className="text-[#F7FAFC]">{shipFree ? "Free" : "£4.99"}</span>
            </div>
            <div className="pg-divider mb-4" />
            <div className="flex justify-between mb-6">
              <span className="text-[#F7FAFC] pg-heading">Total</span>
              <span className="text-[#A6FF00] pg-display text-2xl" style={{ fontFamily: "'Exo 2'" }}>
                £{Math.max(0, netSubtotal + (shipFree ? 0 : 4.99)).toFixed(2)}
              </span>
            </div>
            <Link to="/checkout" className="pg-btn-checkout" data-testid="proceed-to-checkout">
              Proceed to Checkout <ArrowRight size={18} />
            </Link>
            <div className="mt-3 flex items-center justify-center gap-2 pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8]">
              <Lock size={11} className="text-[#00C2D6]" /> Secure encrypted UK checkout
            </div>
          </div>
        </div>
      </div>

      {/* Cross-sell: encourage more items */}
      <RecommendedProducts
        currentSku={null}
        category={items[0]?.category}
        excludeSkus={items.map((i) => i.sku)}
        title="Complete your research stack"
        subtitle="Popular with UK labs ordering today — add another peptide to your basket."
        testid="cart-recommended"
      />
    </div>
  );
}
